import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { getUiView } from './hooks/useUiView';
import { bootstrapPortal } from './utils/portal';
import StaffOnly from './components/router/StaffOnly';
// PORTAL deployments: pin the project path BEFORE the router below captures
// the initial location (module-eval order matters). Auth is a real login
// against the in-app portal-api — nothing is baked.
bootstrapPortal();
import Layout from './pages/Layout';
import DataDictionary from './pages/DataDictionary';
import NotFound from './pages/NotFound';
import DataElements from './pages/DataElements';
import DictionaryQuality from './pages/DictionaryQuality';
import ElementDetails from './pages/ElementDetails';
import ReferenceListValues from './pages/ReferenceListValues';
import ReferenceValueDetails from './pages/ReferenceValueDetails';
import ReferenceLists from './pages/ReferenceLists';
import SourceMappings from './pages/SourceMappings';
import SourceMappingView from './pages/SourceMappingView';
import DataSources from './pages/DataSources';
import DataSourceView from './pages/DataSourceView';
import StorageDetail from './pages/StorageDetail';
import ACTStatistics from './pages/ACTStatistics';
import ProcessingRules from './pages/ProcessingRules';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Alerts from './pages/Alerts';
import Branches from './pages/Branches';
import Projects from './pages/Projects';
import Organizations from './pages/Organizations';
import Templates from './pages/Templates';
import Fleet from './pages/Fleet';
import Strategies from './pages/Strategies';
import Pipeline from './pages/Pipeline';
import Workflows from './pages/Workflows';
import Reports from './pages/Reports';
import Audit from './pages/Audit';
import CortexAudit from './pages/CortexAudit';
import Docs from './pages/Docs';
import Deployments from './pages/Deployments';
import Apps from './pages/Apps';
import Agents from './pages/Agents';
import Guides from './pages/Guides';
import Models from './pages/Models';
import ModelRun from './pages/ModelRun';
import Board from './pages/Board';
import Feed from './pages/Feed';
import SharedApp from './pages/SharedApp';
// Full-bleed vendored Flow studio (lazy: heavy bundle loads only on /flow).
const FlowStudio = React.lazy(() => import('./flow-studio/mount/FlowStudio'));
// Import the strategy barrel so each kind module self-registers its UI panel.
import './components/strategies';

import DataReviewWorkbench from './pages/DataReviewWorkbench';
import Dashboard from './pages/Dashboard';
import LoginPage from './pages/Login';
import MfaVerification from './pages/MfaVerification';
import { Toaster } from "@/components/ui/sonner";
import { ConfirmProvider } from "@/components/ui/confirm-dialog";
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/router/ProtectedRoute';
import PublicRoute from './components/router/PublicRoute';
import RedirectToFirstProject from './components/router/RedirectToFirstProject';
import RouteErrorBoundary from './components/router/RouteErrorBoundary';
import FlowEmbedGuard from './components/router/FlowEmbedGuard';
import { ProjectProvider } from './contexts/ProjectContext';

// The URL is the SINGLE SOURCE OF TRUTH for the active project + branch. Every
// in-app page lives under /p/:projectSlug/b/:branch/<page>. Project-less paths
// (e.g. "/") redirect into the first available project. /login, /mfa and the bare
// /flow-embed iframe route stay OUTSIDE the project prefix.
// Project root. Dashboard is a data-steward surface (element lifecycle,
// mapping counts) hidden from the Simple view's nav — Simple lands on Apps.
const ProjectHome = () => (getUiView() === 'simple' ? <Navigate to="apps" replace /> : <Dashboard />);

const appChildren = [
  { index: true, element: <ProjectHome /> },
  { path: 'dashboard', element: <Dashboard /> },
  { path: 'data-dictionary', element: <DataDictionary /> },
  { path: 'data-elements', element: <DataElements /> },
  { path: 'dictionary-quality', element: <DictionaryQuality /> },
  { path: 'element-details', element: <ElementDetails /> },
  { path: 'reference-lists', element: <ReferenceLists /> },
  { path: 'reference-list-values', element: <ReferenceListValues /> },
  { path: 'reference-lists/:listId/values/:valueId', element: <ReferenceValueDetails /> },
  { path: 'source-mappings', element: <SourceMappings /> },
  { path: 'source-mappings/:id', element: <SourceMappingView /> },
  { path: 'data-sources', element: <DataSources /> },
  { path: 'data-sources/:id', element: <DataSourceView /> },
  { path: 'storages/:origin/:id', element: <StorageDetail /> },
  { path: 'data-sources/:id/statistics', element: <ACTStatistics /> },
  { path: 'processing-rules', element: <ProcessingRules /> },
  { path: 'data-review', element: <DataReviewWorkbench /> },
  { path: 'settings', element: <Settings /> },
  { path: 'profile', element: <Profile /> },
  { path: 'alerts', element: <Alerts /> },
  { path: 'branches', element: <Branches /> },
  { path: 'projects', element: <Projects /> },
  // Organizations = the access-policy manager (orgs, members, roles, project
  // ownership). Server-side enforced; the page hides admin controls per role.
  { path: 'organizations', element: <Organizations /> },
  // App Templates control panel (platform_admin; server-enforced).
  { path: 'templates', element: <Templates /> },
  // Agent fleet: the platform model-routing registry (deverification phase 0).
  // Staff-gated like /flow and /cortex-audit (mindshine domain, any role);
  // the /api/fleet routes accept any authenticated user, like /api/audit/engine.
  { path: 'fleet', element: <StaffOnly><Fleet /></StaffOnly> },
  // Cortex Audit: the PLATFORM's own compliance program (repo-level audits,
  // the Mindshine control register + evidence, the infrastructure layer) —
  // cross-project, so it lives outside any project's audit page. Staff-gated
  // like /flow: hiding the nav item alone would not stop a typed URL.
  { path: 'cortex-audit', element: <StaffOnly><CortexAudit /></StaffOnly> },
  { path: 'pipeline', element: <Pipeline /> },
  // Board: the kanban over the caller's app/agent requests (cross-project).
  { path: 'board', element: <Board /> },
  // Feed: the cortex-wide Slack knowledge surface (digest topics + opt-in
  // channel registry). Cross-project like Board.
  { path: 'feed', element: <Feed /> },
  // Apps: the project's published views. apps lists them; apps/:viewName
  // renders one (embedded flow-embed iframe). :viewName is the .view endpoint name.
  { path: 'apps', element: <Apps /> },
  { path: 'apps/:viewName', element: <Apps /> },
  // Agents: the project's first-class agents (separated from apps).
  { path: 'agents', element: <Agents /> },
  // Guides: the project's saved in-product walkthroughs (spotlight tours),
  // listed + launched here; built via app_requests (new_guide|change_guide).
  { path: 'guides', element: <Guides /> },
  // Models: first-class CUSTOM ML — versions, metrics, the training-data
  // lineage pin, feature importances, promotion. Backed by /api/models and the
  // thin /api/ml/engine proxy. NOT agents: a model is deterministic and
  // versioned (docs/ml-models-surface.md).
  { path: 'models', element: <Models /> },
  // Model runner: try one record against the ACTIVE version — the input
  // contract with real examples, the score + per-run timing, and every place
  // the model is used. Own URL so it opens in a separate tab.
  { path: 'models/:modelName/run', element: <ModelRun /> },
  // Workflows: first-class business processes — My Work inbox + the visual
  // process builder (ReactFlow canvas) + roles. Backed by /api/wf/engine.
  { path: 'workflows', element: <Workflows /> },
  // Reports: pixel-perfect documents (Typst → PDF) over the project's data.
  { path: 'reports', element: <Reports /> },
  // Audit: first-class per-project compliance (runs, findings, issues, fix
  // history; platform layer inherited from the Prowler scanner).
  { path: 'audit', element: <Audit /> },
  // Docs: the per-project documentation surface — Specification (generated
  // living spec) + Knowledge Base (concept notes, source digests), both over
  // the fork's vault. Backed by /api/docs/engine.
  { path: 'docs', element: <Docs /> },
  // Deployments: ship the project as a standalone app (own infra + domain,
  // per-deployment image history). Backed by /api/deployments (Flow exporter).
  { path: 'deployments', element: <Deployments /> },
  // Flow studio, embedded inside the cortex Layout. Splat so its internal routes work.
  // STAFF ONLY: hiding the nav item is not access control — a typed /flow URL
  // would still load the engine editor — so the route itself is gated too.
  {
    path: 'flow/*',
    element: (
      <StaffOnly>
        <React.Suspense fallback={null}>
          <FlowStudio />
        </React.Suspense>
      </StaffOnly>
    ),
  },
  { path: 'strategies', element: <Strategies /> },
  { path: 'strategies/:kind', element: <Strategies /> },
  { path: '*', element: <NotFound /> },
];

// errorElement must sit on each PAGE route, not on the Layout wrapper: an error
// bubbles to the nearest route that has one, and rendering it there replaces
// that route's element — per-page placement keeps the Layout (nav) alive so the
// user can navigate away from a crashed page.
const appRoutes = appChildren.map((route) => ({ ...route, errorElement: <RouteErrorBoundary /> }));

const router = createBrowserRouter([
  {
    element: <PublicRoute />,
    // Full-screen catch-all for errors above the page level (providers, Layout).
    errorElement: <RouteErrorBoundary fullScreen />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/mfa-verification', element: <MfaVerification /> },
    ],
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary fullScreen />,
    children: [
      // Bare Flow embed — NO cortex Layout, NO project prefix (it addresses a
      // Flow project id, not a cortex slug). Used inside iframes
      // (src=/flow-embed/projects/:id/endpoints/:name/flow).
      {
        path: 'flow-embed/*',
        element: (
          <FlowEmbedGuard>
            <div style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
              <React.Suspense fallback={null}>
                <FlowStudio embed />
              </React.Suspense>
            </div>
          </FlowEmbedGuard>
        ),
      },
      // Project-less landing → redirect into the first available project.
      { index: true, element: <RedirectToFirstProject /> },
      // Standalone, chrome-free share target (NO Layout) — slug-addressed so
      // the shared link carries the project slug, not the flow project id.
      // Deliberately OUTSIDE ProjectProvider: a share recipient usually can't
      // list the project, and the provider's unknown-slug guard would rewrite
      // the URL to their first project (breaking the link). SharedApp only
      // needs the URL params + /api/apps/resolve, which authorizes app-level.
      { path: 'p/:projectSlug/b/:branch/share/apps/:viewName', element: <SharedApp /> },
      // Everything else lives under the project + branch path segments. The
      // ProjectProvider reads them from the URL and guards unknown slugs.
      {
        path: 'p/:projectSlug/b/:branch',
        element: <ProjectProvider />,
        children: [
          { element: <Layout />, children: appRoutes },
        ],
      },
      // A project path missing the branch segment → the provider redirects; but
      // also catch /p/:projectSlug alone.
      {
        path: 'p/:projectSlug',
        element: <ProjectProvider />,
        children: [
          { element: <Layout />, children: appRoutes },
        ],
      },
      // Any other unmatched top-level path → landing redirect.
      { path: '*', element: <RedirectToFirstProject /> },
    ],
  },
]);

const Main = () => {
  // No React.StrictMode: its dev-only double-invocation of effects opened the
  // embedded Flow studio's WebSocket twice, so AUTHENTICATE authed one socket
  // while GET_RECORD could go out on the other (un-authed) one — intermittently
  // empty flow canvas on repeat navigation. (StrictMode is stripped in builds,
  // so this only affected local dev, but removing it keeps dev == prod.)
  return (
    <ThemeProvider>
      <AuthProvider>
        <ConfirmProvider>
          <RouterProvider router={router} />
          <Toaster />
        </ConfirmProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default Main;
